import { useState } from 'react';
import {
  Map,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  ArrowLeft,
} from 'lucide-react';
import {
  useTerritories,
  useCreateTerritory,
  useUpdateTerritory,
  useDeleteTerritory,
  useAutoAssign,
  type Territory,
  type TerritoryRule,
  type TerritoryRuleOperator,
} from '../hooks/useTerritories';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface TerritoriesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const fieldOptions = [
  { value: 'state', label: 'State' },
  { value: 'city', label: 'City' },
  { value: 'zipCode', label: 'Zip Code' },
  { value: 'industry', label: 'Industry' },
  { value: 'companySize', label: 'Company Size' },
  { value: 'source', label: 'Source' },
  { value: 'vertical', label: 'Vertical' },
];

const operatorOptions: { value: TerritoryRuleOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'in_list', label: 'is in list' },
];

type ViewMode = 'list' | 'form' | 'test';

// --- Territory Form ---

interface TerritoryFormProps {
  initial?: Territory;
  onSave: (data: Partial<Territory>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function TerritoryForm({ initial, onSave, onCancel, isSaving }: TerritoryFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [rules, setRules] = useState<TerritoryRule[]>(initial?.rules ?? []);

  function addRule() {
    setRules([...rules, { field: 'state', operator: 'equals', value: '' }]);
  }

  function updateRule(index: number, patch: Partial<TerritoryRule>) {
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRule(index: number) {
    setRules(rules.filter((_, i) => i !== index));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, description: description || undefined, isActive, rules });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Territory Details</h3>
        <div>
          <label className="label">Name</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Northeast Region"
            required
          />
        </div>
        <div>
          <label className="label">Description</label>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={2}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="territory-active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-surface-300"
          />
          <label htmlFor="territory-active" className="text-sm text-slate-700">Active</label>
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Rules</h3>
          <button type="button" onClick={addRule} className="btn-secondary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Rule
          </button>
        </div>

        {rules.length === 0 ? (
          <p className="text-xs text-slate-400">No rules defined. Add a rule to match records to this territory.</p>
        ) : (
          <div className="space-y-2">
            {rules.map((rule, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  className="input w-40"
                  value={rule.field}
                  onChange={(e) => updateRule(i, { field: e.target.value })}
                >
                  {fieldOptions.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
                <select
                  className="input w-44"
                  value={rule.operator}
                  onChange={(e) => updateRule(i, { operator: e.target.value as TerritoryRuleOperator })}
                >
                  {operatorOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="input flex-1"
                  value={rule.value}
                  onChange={(e) => updateRule(i, { value: e.target.value })}
                  placeholder="Value"
                />
                <button
                  type="button"
                  onClick={() => removeRule(i)}
                  className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" disabled={isSaving || !name.trim()}>
          {isSaving ? 'Saving...' : initial ? 'Update Territory' : 'Create Territory'}
        </button>
      </div>
    </form>
  );
}

// --- Test Territory Panel ---

function TestTerritoryPanel({ onBack }: { onBack: () => void }) {
  const autoAssign = useAutoAssign();
  const [testState, setTestState] = useState('');
  const [testCity, setTestCity] = useState('');
  const [testIndustry, setTestIndustry] = useState('');

  function handleTest(e: React.FormEvent) {
    e.preventDefault();
    autoAssign.mutate({
      state: testState || undefined,
      city: testCity || undefined,
      industry: testIndustry || undefined,
    });
  }

  return (
    <div className="p-6 space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to territories
      </button>

      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Test Territory Matching</h3>
        <p className="text-xs text-slate-500">Enter record details to see which territory matches.</p>
        <form onSubmit={handleTest} className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">State</label>
              <input className="input" value={testState} onChange={(e) => setTestState(e.target.value)} placeholder="e.g., NY" />
            </div>
            <div>
              <label className="label">City</label>
              <input className="input" value={testCity} onChange={(e) => setTestCity(e.target.value)} placeholder="e.g., New York" />
            </div>
            <div>
              <label className="label">Industry</label>
              <input className="input" value={testIndustry} onChange={(e) => setTestIndustry(e.target.value)} placeholder="e.g., Real Estate" />
            </div>
          </div>
          <button type="submit" className="btn-primary btn-sm" disabled={autoAssign.isPending}>
            <Search className="h-3.5 w-3.5" />
            {autoAssign.isPending ? 'Testing...' : 'Test Match'}
          </button>
        </form>

        {autoAssign.data && (
          <div className={cn('mt-4 rounded-lg p-4', autoAssign.data.matched ? 'bg-green-50' : 'bg-slate-50')}>
            {autoAssign.data.matched ? (
              <div className="text-sm">
                <span className="font-medium text-green-700">Match found:</span>{' '}
                <span className="text-slate-700">{autoAssign.data.territoryName}</span>
                {autoAssign.data.assigneeName && (
                  <span className="text-slate-500"> &mdash; assigned to {autoAssign.data.assigneeName}</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-500">No territory matched these criteria.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main View ---

export function TerritoriesView({ onNavigate }: TerritoriesViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [editingTerritory, setEditingTerritory] = useState<Territory | null>(null);

  const { data, isLoading, error } = useTerritories();
  const createTerritory = useCreateTerritory();
  const updateTerritory = useUpdateTerritory();
  const deleteTerritory = useDeleteTerritory();

  const territories = data?.territories ?? [];

  function handleCreate() {
    setEditingTerritory(null);
    setMode('form');
  }

  function handleEdit(territory: Territory) {
    setEditingTerritory(territory);
    setMode('form');
  }

  function handleSave(formData: Partial<Territory>) {
    if (editingTerritory) {
      updateTerritory.mutate(
        { id: editingTerritory.id, data: formData },
        { onSuccess: () => setMode('list') },
      );
    } else {
      createTerritory.mutate(formData, { onSuccess: () => setMode('list') });
    }
  }

  function handleDelete(id: string) {
    deleteTerritory.mutate(id);
  }

  // --- Test Mode ---
  if (mode === 'test') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Territories</h1>
            <p className="mt-0.5 text-sm text-slate-500">Test territory matching</p>
          </div>
        </div>
        <TestTerritoryPanel onBack={() => setMode('list')} />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingTerritory ? 'Edit Territory' : 'New Territory'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingTerritory ? 'Update territory rules' : 'Define a new territory'}
            </p>
          </div>
        </div>
        <TerritoryForm
          initial={editingTerritory ?? undefined}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createTerritory.isPending || updateTerritory.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Territories</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} territor${data.total !== 1 ? 'ies' : 'y'}` : 'Geographic and rule-based territories'}
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setMode('test')}>
            <Search className="h-4 w-4" />
            Test Match
          </button>
          <button className="btn-primary" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            New Territory
          </button>
        </div>
      </div>

      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load territories. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-4 w-40 animate-pulse rounded bg-surface-200" />
                <div className="h-4 w-24 animate-pulse rounded bg-surface-100" />
              </div>
            ))}
          </div>
        ) : territories.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-500 bg-surface-50">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Assignee</div>
              <div className="col-span-2">Rules</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {territories.map((t) => (
              <div key={t.id} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
                <div className="col-span-3 text-sm font-medium text-slate-900">{t.name}</div>
                <div className="col-span-3 text-sm text-slate-600">
                  {t.assigneeName ?? <span className="text-slate-400">Unassigned</span>}
                </div>
                <div className="col-span-2 text-sm text-slate-600">{t.rules.length} rule{t.rules.length !== 1 ? 's' : ''}</div>
                <div className="col-span-2">
                  <Badge color={t.isActive ? 'green' : 'gray'} size="sm">
                    {t.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleEdit(t)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Map}
            title="No territories"
            description="Create territories to automatically assign records based on rules."
            actionLabel="Create Territory"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
